import React, { forwardRef } from 'react'
import { components } from 'react-select'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import PropTypes from 'prop-types'
import { ErrorMessage, Field } from 'formik'
import { flatten, find } from 'lodash'

import { t } from 'translations'
import { getFieldState } from 'services/Forms'
import { filterOptions } from 'services/Utilities'

const MultiSelect = ({ customLabelElement, noOptionsCustomElement, fieldClass, handleChange, hideDescription, siblings, returnFullValue, ...props }, ref) => {
  const SelectComponent = props.creatable ? CreatableSelect : AsyncSelect

  const DropdownIndicator = props => (
    <components.DropdownIndicator {...props}>
      <i className="fas fa-chevron-down pointer-cursor"></i>
    </components.DropdownIndicator>
  )

  const ClearIndicator = props => (
    <components.ClearIndicator {...props}>
      <i className="fas fa-times pointer-cursor"></i>
    </components.ClearIndicator>
  )

  // Function that returns a format for the option label and handles icons if relevant
  const optionLabel = ({ icon, label, subLabel, color }) => {
    return (
      <>
        <div>
          {icon ? <i className={`fas ${icon} mr-1`} style={{ color: color ?? '' }}></i> : null}
          {label}
        </div>
        {subLabel ? <div>{subLabel}</div> : null}
      </>
    )
  }

  return (
    <Field {...props}>
      {({ field, form }) => {
        if (props.isMulti && !field?.value) field.value = []
        const multipleValues = Array.isArray(field.value)

        // The flatten handles grouped options
        const flatOptions = flatten(
          props.options.map(option => {
            if (option.options) return option.options
            return option
          })
        )

        // Find the selected value in the options
        const parsedSelectedValues = multipleValues
          ? field.value.map(value => {
              return find(flatOptions, { value: typeof value === 'object' ? value.value : value })
            })
          : find(flatOptions, { value: field.value })

        const fieldState = getFieldState(form, field)

        const labelTranslation = customLabelElement || t(`${props.module}.${(props.translation || props.name).toUpperCase()}.LABEL`)

        return (
          <div className={`form-group ${fieldState} ${props.required ? 'required' : ''} ${fieldClass}`}>
            {labelTranslation ? <label className="control-label">{labelTranslation}</label> : null}
            <SelectComponent
              ref={ref}
              components={{ DropdownIndicator, ClearIndicator }}
              noOptionsMessage={() => noOptionsCustomElement ?? t('components.hooks.NO_OPTION')}
              placeholder={
                props.placeholder || (
                  <>
                    <i className="fa fa-search mr-1" />
                    {t('components.hooks.PLACEHOLDER')}
                  </>
                )
              }
              isDisabled={props.disabled}
              formatOptionLabel={optionLabel}
              async
              defaultOptions
              loadOptions={async input => filterOptions(input, props.options, 40)}
              {...props}
              {...field}
              styles={{
                control: (provided, state) => {
                  let style = { ...provided }

                  // Set border color with form state
                  style.borderColor = form.errors[field.name] && form.touched[field.name] ? '#c54f4f' : form.touched[field.name] ? '#009775' : '#ced4da'

                  // Set disabled style
                  if (state.isDisabled) {
                    style.color = '#495057'
                    style.backgroundColor = '#e9ecef'
                    style.border = '1px solid #ced4da'
                  }

                  return style
                },
                singleValue: (provided, state) => {
                  let style = { ...provided }

                  // Set disabled style
                  if (state.isDisabled) {
                    style.color = '#495057'
                  }

                  return style
                },
                multiValue: (styles, state) => {
                  let newStyle = { ...styles, cursor: 'default' }
                  if (state.data.isFixed) newStyle.backgroundColor = 'gray'
                  return newStyle
                },
                multiValueLabel: (styles, state) => {
                  let newStyle = { ...styles, cursor: 'default' }
                  if (state.data.isFixed) {
                    newStyle.color = 'white'
                    newStyle.paddingRight = 6
                  }
                  return newStyle
                },
                multiValueRemove: (styles, state) => {
                  let newStyle = { ...styles, cursor: 'pointer' }
                  if (state.data.isFixed) {
                    newStyle.display = 'none'
                  }
                  return newStyle
                }
              }}
              // Set the field as touched in formik (to be able to display the field validation state)
              onBlur={() => form.setFieldTouched(props.name, true)}
              // Set the new value, we want to replace "null" for an empty array
              onChange={(values = [], actionMeta = {}) => {
                // Prevent removing fixed values
                if (multipleValues) {
                  switch (actionMeta?.action) {
                    case 'remove-value':
                    case 'pop-value':
                      if (actionMeta?.removedValue?.isFixed) {
                        return
                      }
                      break
                    case 'clear':
                      values = actionMeta?.removedValues.filter(v => v.isFixed)
                      break
                    default:
                  }
                }

                let parsedValue

                // Handle parsing of value depending on type of multi-select
                if (props.isClearable && !values) {
                  parsedValue = multipleValues ? [] : props.defaultValue ?? null
                } else if (multipleValues)
                  parsedValue = !values
                    ? []
                    : values.map(value => {
                        if (returnFullValue) return value
                        else return value.value
                      })
                else parsedValue = values.value

                form.setFieldValue(props.name, parsedValue)

                // Send handler after change to component using multiSelect
                if (handleChange) handleChange(parsedValue)
              }}
              value={parsedSelectedValues}
              onCreateOption={props.onCreateOption}
            />
            {siblings}
            {!hideDescription && (
              <small className="form-text text-muted">{t(`${props.module}.${(props.translation || props.name).toUpperCase()}.DESCRIPTION`)}</small>
            )}
            <ErrorMessage name={props.name} render={msg => <span className="text-danger"> {t(msg)}</span>} />
          </div>
        )
      }}
    </Field>
  )
}

export default forwardRef(MultiSelect)

// Set the proptypes for validation
MultiSelect.propTypes = {
  module: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  required: PropTypes.bool,
  hideDescription: PropTypes.bool,
  isMulti: PropTypes.bool,
  siblings: PropTypes.element,
  customLabelElement: PropTypes.element
}
MultiSelect.defaultProps = {
  required: false
}
